import { Behavior, BehaviorConfig } from './Behavior';
import { Entity } from '../base/Entity';
import { MovementState } from '../base/Unit';

export interface WalkerConfig extends BehaviorConfig {
  // REQUIRED
  speed: number;               // Movement speed (pixels/sec)

  // REQUIRED if patrolling (autoPatrol !== false)
  patrolRange?: number;        // Fixed patrol range (use this OR min/max)
  patrolRangeMin?: number;     // Minimum patrol range (random between min-max)
  patrolRangeMax?: number;     // Maximum patrol range (random between min-max)

  // Optional behavior flags (default: false for features, true for autoPatrol)
  autoPatrol?: boolean;        // Start patrolling automatically
  gravity?: number;            // Optional gravity override

  // Flee configuration - fleeRange/fleeSpeedMultiplier REQUIRED if canFlee=true
  canFlee?: boolean;
  fleeRange?: number;
  fleeSpeedMultiplier?: number;

  // Climb configuration - maxClimbAngle REQUIRED if canClimb=true
  canClimb?: boolean;
  maxClimbAngle?: number;

  // Idle (pausas aleatorias mientras patrulla)
  idleChance?: number;         // Probabilidad por segundo de entrar a idle (0..1)
  idleMinMs?: number;          // Duración mínima del idle
  idleMaxMs?: number;          // Duración máxima del idle
  idleCooldownMs?: number;     // Tiempo mínimo entre idles
}

/**
 * WalkerBehavior - Ground movement for entities
 *
 * Used by: Cow, Soldier, RastaFarmer, etc.
 *
 * Features:
 * - Horizontal movement
 * - Patrol within range
 * - Collision-based direction change (listens to 'platformCollisionSide')
 * - Chase target functionality
 * - Flee from player functionality
 *
 * REQUIRED CONFIG (must come from JSON, no defaults):
 * - speed: number (always required)
 * - patrolRange OR patrolRangeMin/Max: number (if autoPatrol !== false)
 * - fleeRange, fleeSpeedMultiplier: number (if canFlee === true)
 * - maxClimbAngle: number (if canClimb === true)
 */
export class WalkerBehavior extends Behavior {
  public speed: number;
  public baseSpeed: number; // Original speed (for restoring after flee)
  public patrolRange: number;
  public direction: number = 1; // 1 = right, -1 = left
  public startX: number;
  public isPatrolling: boolean = false;
  public isChasing: boolean = false;
  public chaseTarget?: Entity;

  // Flee properties
  public canFlee: boolean;
  public fleeRange: number;
  public fleeSpeedMultiplier: number;
  public isFleeing: boolean = false;
  public fleeTarget?: Entity; // Usually the player

  // Climb properties (for slope handling)
  public canClimb: boolean;
  public maxClimbAngle: number; // degrees

  // Abduction state - used to know when to resume after landing
  private stoppedByAbduction: boolean = false;

  // Idle state (pausas cortas para dar variedad)
  private idleChance: number = 0;
  private idleMinMs: number = 0;
  private idleMaxMs: number = 0;
  private idleCooldownMs: number = 0;
  private isIdling: boolean = false;
  private idleUntil: number = 0;
  private nextIdleAllowedAt: number = 0;

  constructor(entity: Entity, config: WalkerConfig) {
    super(entity, config);

    const entityName = entity.texture?.key || 'unknown';
    let hasError = false;

    // LOUD FAIL: Validate required config - speed is ALWAYS required
    if (config.speed === undefined) {
      console.error('[WalkerBehavior] Missing required config: speed', {
        entity: entityName,
        fix: 'Add behaviors.walker.speed to entity JSON',
      });
      hasError = true;
    }

    // Check patrol range if autoPatrol is enabled (default: true)
    const willPatrol = config.autoPatrol !== false;
    if (willPatrol) {
      const hasPatrolRange = config.patrolRange !== undefined ||
        (config.patrolRangeMin !== undefined && config.patrolRangeMax !== undefined);
      if (!hasPatrolRange) {
        console.error('[WalkerBehavior] Missing required config: patrolRange (or patrolRangeMin/Max)', {
          entity: entityName,
          fix: 'Add behaviors.walker.patrolRange to entity JSON, or set autoPatrol: false',
        });
        hasError = true;
      }
    }

    // Check flee config if canFlee is enabled
    if (config.canFlee === true) {
      if (config.fleeRange === undefined) {
        console.error('[WalkerBehavior] Missing required config: fleeRange (canFlee is true)', {
          entity: entityName,
          fix: 'Add behaviors.walker.fleeRange to entity JSON',
        });
        hasError = true;
      }
      if (config.fleeSpeedMultiplier === undefined) {
        console.error('[WalkerBehavior] Missing required config: fleeSpeedMultiplier (canFlee is true)', {
          entity: entityName,
          fix: 'Add behaviors.walker.fleeSpeedMultiplier to entity JSON',
        });
        hasError = true;
      }
    }

    // Check climb config if canClimb is enabled
    if (config.canClimb === true) {
      if (config.maxClimbAngle === undefined) {
        console.error('[WalkerBehavior] Missing required config: maxClimbAngle (canClimb is true)', {
          entity: entityName,
          fix: 'Add behaviors.walker.maxClimbAngle to entity JSON',
        });
        hasError = true;
      }
    }

    if (hasError) {
      // Disable behavior - entity won't walk
      this.enabled = false;
      this.speed = 0;
      this.baseSpeed = 0;
      this.patrolRange = 0;
      this.startX = entity.x;
      this.canFlee = false;
      this.fleeRange = 0;
      this.fleeSpeedMultiplier = 1;
      this.canClimb = false;
      this.maxClimbAngle = 0;
      return;
    }

    // All validations passed - assign values
    this.speed = config.speed;
    this.baseSpeed = this.speed;
    this.patrolRange = this.calculatePatrolRange(config);
    this.startX = entity.x;

    // Flee configuration (already validated if canFlee is true)
    this.canFlee = config.canFlee ?? false;
    this.fleeRange = config.fleeRange ?? 0;
    this.fleeSpeedMultiplier = config.fleeSpeedMultiplier ?? 1;

    // Climb configuration (already validated if canClimb is true)
    this.canClimb = config.canClimb ?? false;
    this.maxClimbAngle = config.maxClimbAngle ?? 0;

    // Apply gravity if specified
    if (config.gravity !== undefined && entity.body) {
      (entity.body as Phaser.Physics.Arcade.Body).setGravityY(config.gravity);
    }

    // Config de idle (opcional)
    this.idleChance = config.idleChance ?? 0;
    this.idleMinMs = config.idleMinMs ?? 0;
    this.idleMaxMs = config.idleMaxMs ?? 0;
    this.idleCooldownMs = config.idleCooldownMs ?? 0;
    if (this.idleChance > 0 && (this.idleMinMs <= 0 || this.idleMaxMs <= 0 || this.idleMaxMs < this.idleMinMs)) {
      console.error('[WalkerBehavior] Config idle inválida', {
        entity: entityName,
        fix: 'Define idleMinMs/idleMaxMs válidos o pon idleChance: 0',
      });
      this.idleChance = 0;
    }

    // Listen for side collision events from PlatformBehavior
    this.scene.events.on('platformCollisionSide', this.onPlatformCollisionSide, this);

    // Listen for abduction events to stop/resume walking
    this.scene.events.on('abductionStarted', this.onAbductionStarted, this);
    this.scene.events.on('entityLanded', this.onEntityLanded, this);

    // Initialize namespaced state for behavior-to-behavior communication
    this.entity.state.behaviors.walker = {
      isStopped: false,
      isChasing: false,
      isPatrolling: false,
      isFleeing: false,
    };

    // Auto-start patrol if configured
    if (willPatrol) {
      this.startPatrol();
    }
  }

  /**
   * Update namespaced state for other behaviors to read
   */
  private updateState(): void {
    const state = this.entity.state.behaviors.walker;
    if (state) {
      state.isStopped = this.isStopped;
      state.isChasing = this.isChasing;
      state.isPatrolling = this.isPatrolling;
      state.isFleeing = this.isFleeing;
    }
  }

  /**
   * When entity starts being abducted, stop walking
   */
  private onAbductionStarted(data: { entity: Entity }): void {
    if (data.entity !== this.entity) return;
    this.stoppedByAbduction = true;
    this.stop();
  }

  /**
   * When entity lands after falling, resume walking if was abducted
   * NOTE: resume() ya NO llama startPatrol() - mantiene el startX original
   */
  private onEntityLanded(data: { entity: Entity }): void {
    if (data.entity !== this.entity) return;
    if (this.stoppedByAbduction) {
      this.stoppedByAbduction = false;
      this.resume();
    }
  }

  /**
   * Handle side collision from PlatformBehavior
   * Only turns around once per collision (uses direction to track)
   */
  private onPlatformCollisionSide(data: { entity: Entity; direction: string }): void {
    if (data.entity !== this.entity) return;
    if (!this.canClimb) {
      // Only turn if we're moving in the same direction as the collision
      const collidingRight = data.direction === 'right' && this.direction === 1;
      const collidingLeft = data.direction === 'left' && this.direction === -1;
      if (collidingRight || collidingLeft) {
        this.turnAround();
      }
    }
  }

  /**
   * Calculate patrol range - supports fixed value or random between min/max
   * NOTE: Config is already validated in constructor, patrolRange MUST exist
   */
  private calculatePatrolRange(config: WalkerConfig): number {
    // If min and max are defined, use random between them
    if (config.patrolRangeMin !== undefined && config.patrolRangeMax !== undefined) {
      const min = config.patrolRangeMin;
      const max = config.patrolRangeMax;
      return Math.floor(Math.random() * (max - min + 1)) + min;
    }

    // patrolRange should exist (validated in constructor)
    if (config.patrolRange === undefined) {
      console.error('[WalkerBehavior] calculatePatrolRange called without patrolRange - this is a bug');
      return 0;
    }

    return config.patrolRange;
  }

  // Flag to completely stop movement (used by ShooterBehavior when guarding)
  public isStopped: boolean = false;

  /**
   * Update movement
   */
  override update(_time: number, delta: number): void {
    if (!this.entity.body) return;

    const body = this.entity.body as Phaser.Physics.Arcade.Body;

    // If stopped, ensure velocity stays at 0 and skip all movement logic
    if (this.isStopped) {
      body.setVelocityX(0);
      return;
    }

    // Auto-check flee condition if canFlee is enabled (incluso si está en idle)
    if (this.canFlee && !this.isFleeing) {
      const player = this.entity.scene.registry.get('player') as Entity | undefined;
      if (player && player.active) {
        this.checkFleeCondition(player);
      }
    }

    // Si está en idle, mantenerlo quieto hasta que termine
    if (this.isIdling) {
      const now = this.scene.time.now;
      if (now < this.idleUntil) {
        body.setVelocityX(0);
        return;
      }
      this.stopIdle();
    }

    // Chance de entrar en idle (solo patrullando y sin huir/perseguir)
    if (this.isPatrolling && !this.isFleeing && !this.isChasing && this.idleChance > 0) {
      const now = this.scene.time.now;
      if (now >= this.nextIdleAllowedAt) {
        const chanceThisFrame = (this.idleChance / 1000) * delta;
        if (Math.random() < chanceThisFrame) {
          this.startIdle();
          body.setVelocityX(0);
          return;
        }
      }
    }

    // Priority: Flee > Chase > Patrol
    if (this.isFleeing && this.fleeTarget) {
      this.updateFlee();
    } else if (this.isChasing && this.chaseTarget) {
      this.updateChase();
    } else if (this.isPatrolling) {
      this.updatePatrol();
    }

    // Apply velocity
    body.setVelocityX(this.speed * this.direction);
  }

  /**
   * Update patrol logic
   */
  private updatePatrol(): void {
    // Turn around at patrol boundaries
    if (this.entity.x > this.startX + this.patrolRange) {
      this.direction = -1;
    } else if (this.entity.x < this.startX - this.patrolRange) {
      this.direction = 1;
    }
  }

  /**
   * Update chase logic
   */
  private updateChase(): void {
    if (!this.chaseTarget || !this.chaseTarget.active) {
      this.stopChasing();
      return;
    }

    // Move toward target
    this.direction = this.chaseTarget.x > this.entity.x ? 1 : -1;
  }

  /**
   * Update flee logic - runs AWAY from target
   */
  private updateFlee(): void {
    if (!this.fleeTarget || !this.fleeTarget.active) {
      this.stopFleeing();
      return;
    }

    // Check if still in flee range
    const distance = this.distanceToPoint(this.fleeTarget.x, this.fleeTarget.y);
    if (distance > this.fleeRange * 1.5) {
      // Out of danger zone, stop fleeing
      this.stopFleeing();
      return;
    }

    // Move AWAY from target (opposite direction of chase)
    this.direction = this.fleeTarget.x > this.entity.x ? -1 : 1;
  }

  /**
   * Start patrolling
   */
  startPatrol(range?: number): void {
    if (range !== undefined) {
      this.patrolRange = range;
    }
    this.startX = this.entity.x;
    this.isPatrolling = true;
    this.isChasing = false;
    this.stopIdle(true);
    this.chaseTarget = undefined;
    this.updateEntityMovementState('walking');
    this.updateState();
  }

  /**
   * Stop patrolling
   */
  stopPatrol(): void {
    this.isPatrolling = false;
    this.updateState();
  }

  /**
   * Start chasing a target
   */
  startChasing(target: Entity): void {
    this.chaseTarget = target;
    this.isChasing = true;
    this.isPatrolling = false;
    this.stopIdle(true);
    this.updateEntityMovementState('running');
    this.updateState();
  }

  /**
   * Stop chasing
   */
  stopChasing(): void {
    this.isChasing = false;
    this.chaseTarget = undefined;
    this.updateEntityMovementState('idle');
    this.updateState();
  }

  /**
   * Check if should flee from a target (usually the player)
   * Call this from the entity's update with the player reference
   * Returns true if flee was triggered
   */
  checkFleeCondition(target: Entity): boolean {
    if (!this.canFlee) return false;
    if (this.isFleeing) return true; // Already fleeing

    const distance = this.distanceToPoint(target.x, target.y);
    if (distance <= this.fleeRange) {
      this.startFleeing(target);
      return true;
    }
    return false;
  }

  /**
   * Start fleeing from a target
   */
  startFleeing(target: Entity): void {
    if (!this.canFlee) return;

    this.fleeTarget = target;
    this.isFleeing = true;
    this.isPatrolling = false;
    this.isChasing = false;
    this.stopIdle(true);
    this.chaseTarget = undefined;

    // Increase speed when fleeing
    this.speed = this.baseSpeed * this.fleeSpeedMultiplier;

    // Update entity movement state to running
    this.updateEntityMovementState('running');

    // Update namespaced state
    this.updateState();

    // Emit event for animation change (Walking -> Running)
    this.entity.emit('fleeStarted', { target });
  }

  /**
   * Stop fleeing and return to normal behavior
   */
  stopFleeing(): void {
    if (!this.isFleeing) return;

    this.isFleeing = false;
    this.fleeTarget = undefined;

    // Restore normal speed
    this.speed = this.baseSpeed;

    // Return to patrol if was patrolling before
    this.isPatrolling = true;

    // Update entity movement state back to walking
    this.updateEntityMovementState('walking');

    // Update namespaced state
    this.updateState();

    // Emit event for animation change (Running -> Walking)
    this.entity.emit('fleeStopped');
  }

  /**
   * Entrar en idle por un rato
   */
  private startIdle(): void {
    if (this.isIdling) return;
    const now = this.scene.time.now;
    const duration = Math.floor(Math.random() * (this.idleMaxMs - this.idleMinMs + 1)) + this.idleMinMs;
    this.isIdling = true;
    this.idleUntil = now + duration;
    this.nextIdleAllowedAt = this.idleUntil + this.idleCooldownMs;
    this.updateEntityMovementState('idle');
    this.updateState();
  }

  /**
   * Salir del idle (y volver a caminar si corresponde)
   */
  private stopIdle(forceWalking: boolean = false): void {
    if (!this.isIdling && !forceWalking) return;
    this.isIdling = false;
    if (forceWalking || this.isPatrolling) {
      this.updateEntityMovementState('walking');
    }
    this.updateState();
  }

  /**
   * Turn around
   */
  turnAround(): void {
    this.direction *= -1;
  }

  /**
   * Set direction
   */
  setDirection(dir: number): void {
    this.direction = dir > 0 ? 1 : -1;
  }

  /**
   * Move toward a specific X position
   */
  moveToward(targetX: number): void {
    this.direction = targetX > this.entity.x ? 1 : -1;
  }

  /**
   * Stop all movement completely
   * Use resume() to start moving again
   */
  stop(): void {
    this.isStopped = true;
    this.stopIdle(true);
    if (this.entity.body) {
      (this.entity.body as Phaser.Physics.Arcade.Body).setVelocityX(0);
    }
    this.isPatrolling = false;
    this.isChasing = false;
    this.isFleeing = false;
    this.chaseTarget = undefined;
    this.fleeTarget = undefined;
    this.speed = this.baseSpeed; // Restore base speed
    this.updateEntityMovementState('idle');
    this.updateState();
  }

  /**
   * Resume movement after being stopped
   * Typically resumes patrolling
   * NOTE: Ya NO llama startPatrol() porque eso reseteaba startX y causaba el bug de giros
   */
  resume(): void {
    this.isStopped = false;
    this.stopIdle(true);
    // ANTES: this.startPatrol(); ← Esto reseteaba startX = entity.x, causando patrol range de ~2px!
    // AHORA: Solo retomamos el patrol sin resetear el punto de inicio
    this.isPatrolling = true;
    this.isChasing = false;
    this.chaseTarget = undefined;
    this.updateEntityMovementState('walking');
    this.updateState();
  }

  /**
   * Check if entity is moving
   */
  isMoving(): boolean {
    if (!this.entity.body) return false;
    return Math.abs((this.entity.body as Phaser.Physics.Arcade.Body).velocity.x) > 0;
  }

  /**
   * Get distance to a target entity
   */
  distanceTo(target: Entity): number {
    return Math.abs(target.x - this.entity.x);
  }

  /**
   * Get distance to a point (for flee range checks)
   */
  distanceToPoint(x: number, y: number): number {
    const dx = x - this.entity.x;
    const dy = y - this.entity.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Set base speed (also updates current speed if not fleeing)
   */
  setSpeed(speed: number): void {
    this.baseSpeed = speed;
    if (!this.isFleeing) {
      this.speed = speed;
    }
  }

  /**
   * Update entity's movement state if it supports the state system
   * (Unit has setMovement, other entities may not)
   */
  private updateEntityMovementState(state: MovementState): void {
    if (typeof (this.entity as Entity & { setMovement?: (state: MovementState) => void }).setMovement === 'function') {
      (this.entity as Entity & { setMovement: (state: MovementState) => void }).setMovement(state);
    }
  }

  /**
   * Cleanup - remove event listeners and namespaced state
   */
  override destroy(): void {
    this.scene.events.off('platformCollisionSide', this.onPlatformCollisionSide, this);
    this.scene.events.off('abductionStarted', this.onAbductionStarted, this);
    this.scene.events.off('entityLanded', this.onEntityLanded, this);

    // Clean up namespaced state
    delete this.entity.state.behaviors.walker;

    super.destroy();
  }
}
